import { canv } from "@/canv/global";
import { SketchWidget } from "@/elements/sketch";
import { eventEmitter } from "@/eventEmitter";
import { Group } from "@/group";
import { t } from "@/i18n";
import type { PenToolMode } from "@/tools/PenTool/types";
import { toolBase } from "@/tools/toolBase";
import { checkClickOnSketch, checkIntersectionSketchAndMouseLine } from "./utils";

export class PenTool extends toolBase {
    constructor() {
        super();
        eventEmitter.dispatch("penToolMenu.color", { color: this.color });
        eventEmitter.dispatch("penToolMenu.penType", { penType: this.mode });
        eventEmitter.dispatch("penToolMenu.lineWidth", { lineWidth: this.lineWidth });
    }

    getToolView() {
        return `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.9734 5.97398L15.3872 4.56012C15.5647 4.38263 15.7753 4.24182 16.0072 4.14576C16.239 4.04969 16.4875 4.00024 16.7385 4.00024C16.9895 4.00024 17.238 4.04969 17.4698 4.14576C17.7017 4.24182 17.9124 4.38263 18.0898 4.56012L19.4406 5.91093C19.7988 6.26922 20 6.75511 20 7.26173C20 7.76836 19.7988 8.25425 19.4406 8.61254L18.0267 10.0264M13.9734 5.97398L4.78713 15.1593C4.46995 15.4764 4.27448 15.8951 4.23497 16.3419L4.00378 18.9595C3.99136 19.0986 4.00962 19.2388 4.05726 19.3701C4.10489 19.5015 4.18075 19.6207 4.27949 19.7196C4.37823 19.8184 4.49744 19.8944 4.62873 19.9421C4.76001 19.9899 4.90017 20.0083 5.03933 19.996L7.65688 19.7648C8.10437 19.7257 8.52378 19.5302 8.84146 19.2126L18.0267 10.0264M13.9734 5.97398L18.0267 10.0264" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;
    }

    getToolTipName() {
        return t("tools.drawing");
    }

    getToolHotKey() {
        return "P";
    }

    select() {
        super.select();
        eventEmitter.dispatch("penToolMenu.visibility", { isVisible: true });
        this.isOpen = true;
    }

    unselect() {
        super.unselect();
        this.ghostSketch = null;
        eventEmitter.dispatch("penToolMenu.visibility", { isVisible: false });
        this.isOpen = false;
    }

    reopenOrCloseSubmenu() {
        if (this.isOpen) {
            this.unselect();
        } else {
            this.select();
        }
    }

    setColor(color: string) {
        this.color = color;
    }
    setWidth(width: number) {
        this.lineWidth = width;
    }
    setMode(mode: PenToolMode) {
        this.mode = mode;
    }

    eraseSketchByCrossing() {
        const allSketches = canv.canvasController.findAllWidgetsByConstructorName("sketch");

        for (const sketch of allSketches) {
            if (checkClickOnSketch(sketch) || checkIntersectionSketchAndMouseLine(sketch)) {
                canv.canvasController.deleteProcessor(sketch.parent);
            }
        }

        canv.redraw();
    }

    eraseSketchByClick() {
        const allSketches = canv.canvasController.findAllWidgetsByConstructorName("sketch");
        const deleteSketches: SketchWidget[] = [];

        for (const sketch of allSketches) {
            if (checkClickOnSketch(sketch)) {
                deleteSketches.push(sketch);
            }
        }

        deleteSketches.forEach((sketch) => {
            canv.canvasController.deleteProcessor(sketch.parent);
        });

        canv.redraw();
    }

    TryAction(type) {
        if (this.mode === "eraser") {
            try {
                if (type === "mousedown") {
                    this.isEraser = true;
                    canv.eventManager.openRecord();

                    this.eraseSketchByClick();
                } else if (type === "mousemove" && this.isEraser) {
                    this.eraseSketchByCrossing();
                } else if (type === "mouseup") {
                    canv.eventManager.closeRecord();
                    this.isEraser = false;
                }
            } catch (err) {
                console.error(err);
                canv.captureException(err);
            }

            return;
        }

        if (type === "mousedown") {
            if (window.screen.width <= 640) {
                this.unselect();
            }

            const pt = canv.actionManager.getActionPoint();

            const newPoint = {
                x: pt.x,
                y: pt.y,
            };

            this.ghostSketch = new SketchWidget();
            this.ghostSketch.points.push(newPoint);
            this.ghostSketch.updateDims();
            if (this.mode === "pen" || this.mode === "highlighter") {
                this.ghostSketch.mode = this.mode;
            }
            this.ghostSketch.color = this.color;
            this.ghostSketch.lineWidth = this.lineWidth;
        } else if (type === "mousemove") {
            if (this.ghostSketch == null) {
                return;
            }

            const pt = canv.actionManager.getActionPoint();

            const newPoint = {
                x: pt.x,
                y: pt.y,
            };

            this.ghostSketch.points.push(newPoint);
            this.ghostSketch.updateDims();
        } else if (type === "mouseup") {
            if (!this.ghostSketch) return;

            if (this.ghostSketch.points.length < 5) {
                this.ghostSketch = null;
                return;
            }

            const newGroup = new Group(true);
            this.ghostSketch.setParent(newGroup);
            canv.canvasController.addGroup(newGroup);

            canv.eventManager.openRecord();
            canv.eventManager.createBuildEvent("create", newGroup);
            canv.eventManager.closeRecord();

            this.ghostSketch = null;
        }

        canv.redraw();
    }

    chooseCursor() {
        if (this.mode === "eraser") {
            document.body.style.cursor = `url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAAQCAYAAADwMZRfAAAACXBIWXMAAAhOAAAITgGMMQDsAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAC7SURBVHgBpZILDYMwEEBvyQTgYEhAQudgDloJczAkzMkyBcwBc1AJmwO4S+6SC1ybAy55odznQdMC+CIgL2RAHrAjIjIhP5bQekSarQI9lLaIRJCN5sS1wSMQotHTc62rCeh3W35aosD5UBPIFhpDJLnMH6oKwBDd1frmFVgiIuliC74j05K0LNIx5SMCkYxHBBQ9lO+CS7BsjHsEJzVA26Lb9+Fcx/k/8i3Mv5HnmV+o8UoJ5MK50uAqZjlYShwfXgeFAAAAAElFTkSuQmCC'), auto`;
        } else {
            document.body.style.cursor = "default";
        }
    }

    draw() {
        this.chooseCursor();

        if (this.ghostSketch === null) {
            return;
        }

        this.ghostSketch.draw();
    }

    name = "penTool";
    ghostSketch: SketchWidget | null = null;
    mode: PenToolMode = "pen";
    lineWidth = 6;
    color = "#000000";
    isEraser = false;
    isStartedErase = false;
    isOpen = false;
}
